import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowRight, BarChart3, ChevronDown, ClipboardList, LockKeyhole, RefreshCw, Search, ShieldCheck, UsersRound } from "lucide-react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { getTransparencyCycles, getTransparencyProject, getTransparencyProjects } from "../../lib/performance-lens/review-api.ts";
import type { TransparencyCycle, TransparencyProject, TransparencyProjectDetail, TransparencyReview } from "../../lib/performance-lens/types.ts";
import ScreenSkeleton from "../../components/ui/screen-skeleton/screen-skeleton.tsx";
import styles from "./transparency-page.module.css";
import { Select } from "../../components/ui/select/select.tsx";

const roleNames: Record<string, string> = {
  HOD: "Head of Delivery", DL: "Delivery Lead", PM: "Project Manager", SA: "Solution Architect",
  BA: "Business Analyst", DEV: "Developer", QA: "QA Engineer",
};

function dateLabel(value: string) {
  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${value.slice(0, 10)}T00:00:00Z`));
}

function submittedLabel(value: string) {
  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" })
    .format(new Date(value));
}

function scoreLabel(value: number | null) { return value === null ? "—" : value.toFixed(2); }
function statusLabel(value: string) { return value.charAt(0) + value.slice(1).toLowerCase(); }

function ReviewDetails({ review }: { review: TransparencyReview }) {
  const submitted = review.status === "SUBMITTED";
  return <details className={styles.review}>
    <summary className={styles.reviewSummary}>
      <span className={styles.person}><strong>{review.reviewerName}</strong><small>{roleNames[review.reviewerRole] ?? review.reviewerRole}</small></span>
      <ArrowRight size={16} className={styles.arrow} aria-hidden="true" />
      <span className={styles.person}><strong>{review.revieweeName}</strong><small>{roleNames[review.revieweeRole] ?? review.revieweeRole}</small></span>
      <span className={styles.kind}>{review.kind === "MANAGER" ? "Manager" : review.kind === "SELF" ? "Self check" : "Peer"}</span>
      <span className={review.status === "SUBMITTED" ? styles.submitted : styles.unfinished}>{statusLabel(review.status)}</span>
      <span className={styles.score}>{submitted ? scoreLabel(review.averageRating) : "—"}<small>{submitted && review.averageRating !== null ? " / 5" : submitted ? "No rating" : "No score"}</small></span>
      <ChevronDown size={18} className={styles.chevron} aria-hidden="true" />
    </summary>
    <div className={styles.reviewBody}>
      <div className={styles.reviewMeta}><span>Reviewer ID {review.reviewerId}</span><span>Reviewee ID {review.revieweeId}</span></div>
      {!submitted ? <p className={styles.info}>This review is {statusLabel(review.status).toLowerCase()}. Draft answers and comments are not shown.</p> : <>
        <div className={styles.reviewMeta}>
          <span>Submitted {review.submittedAt ? submittedLabel(review.submittedAt) : "date unavailable"}</span>
          {review.submittedLate && <span className={styles.late}>Late submission</span>}
          {review.notEnoughInteraction && <span className={styles.late}>Not enough interaction</span>}
        </div>
        {review.answers.length ? <div className={styles.answers}>
          {review.answers.map(answer => <div className={styles.answer} key={`${review.id}-${answer.questionKey}`}>
            <span><strong>{answer.label}</strong>{answer.hint && <small>{answer.hint}</small>}</span>
            <span className={styles.answerValue}>{answer.rating} / 5</span>
          </div>)}
        </div> : <p className={styles.info}>No item ratings were submitted for this review.</p>}
        {review.comment?.trim() && <div className={styles.comment}><span>Reviewer comment</span><p>{review.comment}</p></div>}
      </>}
    </div>
  </details>;
}

export default function TransparencyPage() {
  const [cycles, setCycles] = useState<TransparencyCycle[]>([]);
  const [cycleNumber, setCycleNumber] = useState<number | null>(null);
  const [projects, setProjects] = useState<TransparencyProject[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TransparencyProjectDetail | null>(null);
  const [search, setSearch] = useState("");
  const [reviewSearch, setReviewSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [kind, setKind] = useState("ALL");
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoadingCycles(true); setError("");
    void getTransparencyCycles(controller.signal).then(value => {
      if (controller.signal.aborted) return;
      setCycles(value);
      setCycleNumber(current => current !== null && value.some(cycle => cycle.number === current) ? current
        : (value.find(cycle => cycle.status === "OPEN") ?? value[0])?.number ?? null);
    }).catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load cycles."); })
      .finally(() => { if (!controller.signal.aborted) setLoadingCycles(false); });
    return () => controller.abort();
  }, [refreshKey]);

  useEffect(() => {
    if (cycleNumber === null) return;
    const controller = new AbortController();
    setLoadingProjects(true); setError("");
    void getTransparencyProjects(cycleNumber, controller.signal).then(value => {
      if (controller.signal.aborted) return;
      setProjects(value);
      setProjectId(current => current !== null && value.some(project => project.id === current) ? current : value[0]?.id ?? null);
    }).catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load projects."); })
      .finally(() => { if (!controller.signal.aborted) setLoadingProjects(false); });
    return () => controller.abort();
  }, [cycleNumber, refreshKey]);

  useEffect(() => {
    if (cycleNumber === null || projectId === null) return;
    const controller = new AbortController();
    setLoadingDetail(true); setError("");
    void getTransparencyProject(cycleNumber, projectId, controller.signal).then(value => {
      if (!controller.signal.aborted) setDetail(value);
    }).catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load project reviews."); })
      .finally(() => { if (!controller.signal.aborted) setLoadingDetail(false); });
    return () => controller.abort();
  }, [cycleNumber, projectId, refreshKey]);

  const selectedCycle = cycles.find(cycle => cycle.number === cycleNumber);
  const visibleProjects = useMemo(() => projects.filter(project => project.name.toLowerCase().includes(search.trim().toLowerCase())), [projects, search]);
  const visibleReviews = useMemo(() => (detail?.reviews ?? []).filter(review =>
    (status === "ALL" || review.status === status) && (kind === "ALL" || review.kind === kind)
    && `${review.reviewerName} ${review.revieweeName} ${review.reviewerRole} ${review.revieweeRole}`.toLowerCase().includes(reviewSearch.trim().toLowerCase())), [detail, status, kind, reviewSearch]);
  const currentDetail = detail?.cycle.number === cycleNumber && detail.project.id === projectId ? detail : null;

  if (loadingCycles && !cycles.length) return <PageShell className={styles.shell}>
    <ScreenSkeleton label="Loading review cycles" variant="table" />
  </PageShell>;

  return <PageShell className={styles.shell}><main className={styles.page}>
    <section className={styles.controls} aria-label="Review scope">
      <div className={styles.scopeIntro}>
        <div className={styles.scopeTitle}><LockKeyhole size={17} aria-hidden="true" /><h1>Review transparency</h1><span>Named reviews and comments · access audited · employee feedback stays anonymous</span></div>
        <span className={styles.accessChip}><ShieldCheck size={15} aria-hidden="true" /> Configured viewers only</span>
      </div>
      <div className={styles.scopeFields}>
        <Select label="Cycle" labelLayout="inline" value={cycleNumber === null ? "" : String(cycleNumber)} disabled={loadingCycles || !cycles.length} placeholder="No cycles available"
          options={cycles.map(cycle => ({ value: String(cycle.number), label: `Cycle ${cycle.number} · ${cycle.status} · ${dateLabel(cycle.startDate)} – ${dateLabel(cycle.endDate)}` }))}
          onChange={value => { setCycleNumber(Number(value)); setProjectId(null); setProjects([]); setDetail(null); setReviewSearch(""); setStatus("ALL"); setKind("ALL"); }} />
        {selectedCycle && <div className={styles.cycleSummary}><span>{selectedCycle.submittedCount} of {selectedCycle.assignedCount} project reviews submitted</span><span className={styles.cycleStatus}>{statusLabel(selectedCycle.status)}</span></div>}
        <button type="button" className={styles.refresh} onClick={() => { setDetail(null); setRefreshKey(value => value + 1); }}><RefreshCw size={16} /> Refresh</button>
      </div>
    </section>
    {error && <div className={styles.error} role="alert"><AlertCircle size={18} />{error}</div>}
    {!loadingCycles && !cycles.length && !error && <div className={styles.empty}>No Performance Lens cycles are configured yet.</div>}
    {cycleNumber !== null && <div className={styles.workspace}>
      <aside className={styles.projectPanel} aria-label="Projects in selected cycle">
        <div className={styles.panelHeading}><div className={styles.headingIcon}><BarChart3 size={19} /></div><div><h2>Projects</h2><p>{projects.length} with review assignments</p></div></div>
        <label className={styles.search}><Search size={17} aria-hidden="true" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Find a project" aria-label="Find a project" /></label>
        <div className={styles.projectList}>
          {loadingProjects && <ScreenSkeleton label="Loading projects" variant="table" compact />}
          {!loadingProjects && !projects.length && <p className={styles.listMessage}>No project reviews are assigned for this cycle.</p>}
          {!loadingProjects && !!projects.length && !visibleProjects.length && <p className={styles.listMessage}>No projects match your search.</p>}
          {!loadingProjects && visibleProjects.map(project => <button type="button" key={project.id}
            className={project.id === projectId ? styles.projectActive : styles.projectButton}
            aria-current={project.id === projectId ? "true" : undefined}
            onClick={() => { setProjectId(project.id); setDetail(null); setReviewSearch(""); setStatus("ALL"); setKind("ALL"); }}>
            <span className={styles.projectTitle}><strong>{project.name}</strong><ArrowRight size={16} aria-hidden="true" /></span>
            <span className={styles.projectMeta}>{project.submittedCount}/{project.assignedCount} submitted <span>·</span> {project.peopleCount} people</span>
            <span className={styles.progress}><span style={{ width: `${project.assignedCount ? project.submittedCount / project.assignedCount * 100 : 0}%` }} /></span>
          </button>)}
        </div>
      </aside>
      <div className={styles.detailPanel}>
        {loadingDetail && <ScreenSkeleton label="Loading project reviews" variant="table" compact />}
        {!loadingDetail && !currentDetail && !error && <div className={styles.empty}><ClipboardList size={32} /><strong>Select a project</strong><span>Choose a project to inspect its review assignments and submitted ratings.</span></div>}
        {currentDetail && <>
          <div className={styles.detailHead}><div><span className={styles.eyebrow}>Cycle {currentDetail.cycle.number} · Project review record</span><h2>{currentDetail.project.name}</h2><p>{dateLabel(currentDetail.cycle.startDate)} – {dateLabel(currentDetail.cycle.endDate)} · Named reviewer-to-reviewee detail</p></div></div>
          <div className={styles.metrics} aria-label="Project review summary">
            <div><ClipboardList size={18} /><span>Assigned</span><strong>{currentDetail.project.assignedCount}</strong></div>
            <div><ShieldCheck size={18} /><span>Submitted</span><strong>{currentDetail.project.submittedCount}</strong></div>
            <div><UsersRound size={18} /><span>People rated</span><strong>{currentDetail.project.peopleCount}</strong></div>
            <div><BarChart3 size={18} /><span>Average rating</span><strong>{scoreLabel(currentDetail.project.averageRating)}<small>{currentDetail.project.averageRating === null ? "" : " / 5"}</small></strong></div>
          </div>
          <div className={styles.reviewHeading}><div><h3>Who rated whom</h3><p>{currentDetail.reviews.length} assignments · submitted answers expand below</p></div></div>
          <div className={styles.reviewFilters}>
            <label className={styles.search}><Search size={17} aria-hidden="true" /><input value={reviewSearch} onChange={event => setReviewSearch(event.target.value)} placeholder="Search reviewer or reviewee" aria-label="Search reviewer or reviewee" /></label>
            <Select label="Status" value={status} onChange={setStatus} options={[{ value: "ALL", label: "All statuses" }, { value: "SUBMITTED", label: "Submitted" }, { value: "DRAFT", label: "Draft" }, { value: "PENDING", label: "Pending" }]} />
            <Select label="Review type" value={kind} onChange={setKind} options={[{ value: "ALL", label: "All types" }, { value: "PEER", label: "Peer" }, { value: "MANAGER", label: "Manager" }]} />
          </div>
          <div className={styles.reviewList}>
            <div className={styles.reviewColumns}><span>Reviewer</span><span /><span>Reviewee</span><span>Type</span><span>Status</span><span>Avg.</span><span /></div>
            {visibleReviews.map(review => <ReviewDetails key={review.id} review={review} />)}
            {!visibleReviews.length && <p className={styles.listMessage}>No reviews match these filters.</p>}
          </div>
        </>}
      </div>
    </div>}
  </main></PageShell>;
}
